import { Mail, Phone, Clock, MessageCircle } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/contexts/I18nContext';
import { SUPPORT_CONTACT } from '@/lib/constants';

interface ChannelRowProps {
  icon: React.ReactNode;
  iconClass: string;
  label: string;
  value: string;
  href?: string;
  ltr?: boolean;
}

function ChannelRow({ icon, iconClass, label, value, href, ltr }: ChannelRowProps) {
  const body = (
    <>
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${iconClass}`}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="truncate text-sm font-medium" dir={ltr ? 'ltr' : undefined}>
          {value}
        </p>
      </div>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        className="-m-1 flex items-center gap-3 rounded-lg p-1 transition-colors hover:bg-muted"
      >
        {body}
      </a>
    );
  }
  return <div className="-m-1 flex items-center gap-3 p-1">{body}</div>;
}

/** Read-only QuBox support contact channels: WhatsApp, email, phone, hours. */
export function ContactChannels() {
  const { tp } = useI18n();
  const s = tp.support;
  const waDigits = SUPPORT_CONTACT.whatsapp.replace(/\D/g, '');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{s.contactTitle}</CardTitle>
        <CardDescription>{s.contactDescription}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {SUPPORT_CONTACT.whatsapp && (
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-green-100 text-green-600 dark:bg-green-950">
              <MessageCircle className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">{s.whatsappLabel}</p>
              <p className="truncate text-sm font-medium" dir="ltr">
                {SUPPORT_CONTACT.whatsapp}
              </p>
            </div>
            <Button asChild size="sm" variant="secondary">
              <a
                href={`https://wa.me/${waDigits}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {s.whatsappAction}
              </a>
            </Button>
          </div>
        )}

        <ChannelRow
          icon={<Mail className="h-4 w-4" />}
          iconClass="bg-primary/10 text-primary"
          label={s.emailLabel}
          value={SUPPORT_CONTACT.email}
          href={`mailto:${SUPPORT_CONTACT.email}`}
          ltr
        />

        {SUPPORT_CONTACT.phone && (
          <ChannelRow
            icon={<Phone className="h-4 w-4" />}
            iconClass="bg-primary/10 text-primary"
            label={s.phoneLabel}
            value={SUPPORT_CONTACT.phone}
            href={`tel:${SUPPORT_CONTACT.phone.replace(/\s/g, '')}`}
            ltr
          />
        )}

        <ChannelRow
          icon={<Clock className="h-4 w-4" />}
          iconClass="bg-muted text-muted-foreground"
          label={s.hoursLabel}
          value={s.hoursValue}
        />
      </CardContent>
    </Card>
  );
}
